// The browser's side of a passkey ceremony: the server's options in, the
// credential out, both as JSON. The server speaks WebAuthn's JSON forms
// (webauthn-rs, with bytes in base64url), which the browser parses and writes
// itself: `PublicKeyCredential.parseCreationOptionsFromJSON` and
// `parseRequestOptionsFromJSON` for the options, `toJSON()` for the
// credential. The tests and the mock replace this module with a fake
// authenticator (mockPasskeys.ts).
//
// One adaptation: signing in, the server asks for conditional mediation
// (passkey autofill), which webauthn-rs sets for every discoverable sign-in.
// The pages sign in from a button, so they ask in a modal prompt instead; the
// options are the same either way.
import { inMacShell } from '../lib/shell'
import type { CreationOptions, RequestOptions } from './types'

/**
 * Why no passkey came back: the person closed the prompt (or it timed out),
 * the browser has no passkeys, or, in the Mac app, the system refused the
 * request (shellPasskeys.ts).
 */
export type PasskeyProblem = 'cancelled' | 'unsupported' | 'refused'

export class PasskeyError extends Error {
  readonly problem: PasskeyProblem

  constructor(problem: PasskeyProblem, message: string) {
    super(message)
    this.name = 'PasskeyError'
    this.problem = problem
  }
}

/** What the pages ask of the browser's passkeys (in the Mac app, of the system's). */
export interface Passkeys {
  /** Whether this browser makes and uses passkeys, in the JSON forms the server speaks. */
  supported(): boolean
  /** Makes a passkey for the server's options, in the browser's prompt. Rejects with a PasskeyError when none was made. */
  create(options: CreationOptions): Promise<RegistrationResponseJSON>
  /** Asks the person for one of their passkeys, in the browser's prompt. Rejects with a PasskeyError when none was used. */
  get(options: RequestOptions): Promise<AuthenticationResponseJSON>
}

/** The browser's own passkeys, through `navigator.credentials`. */
export const browserPasskeys: Passkeys = {
  supported() {
    const credentials = globalThis.PublicKeyCredential as typeof PublicKeyCredential | undefined
    return (
      globalThis.isSecureContext === true &&
      typeof credentials?.parseCreationOptionsFromJSON === 'function' &&
      typeof credentials.parseRequestOptionsFromJSON === 'function' &&
      typeof credentials.prototype.toJSON === 'function' &&
      typeof navigator.credentials?.create === 'function'
    )
  },

  async create(options) {
    const publicKey = PublicKeyCredential.parseCreationOptionsFromJSON(options.publicKey)
    const credential = await prompt(() => navigator.credentials.create({ publicKey }))
    return credential.toJSON() as RegistrationResponseJSON
  },

  async get(options) {
    // Not `options.mediation`: see above.
    const publicKey = PublicKeyCredential.parseRequestOptionsFromJSON(options.publicKey)
    const credential = await prompt(() => navigator.credentials.get({ publicKey }))
    return credential.toJSON() as AuthenticationResponseJSON
  },
}

/** Runs the browser's prompt; says when it gave no passkey. Any other failure is the browser's, as it is. */
async function prompt(ask: () => Promise<Credential | null>): Promise<PublicKeyCredential> {
  let credential: Credential | null
  try {
    credential = await ask()
  } catch (err) {
    // NotAllowedError is how a browser says the person closed the prompt, or let it time out.
    if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
      throw new PasskeyError('cancelled', err.message)
    }
    if (err instanceof DOMException && err.name === 'NotSupportedError') throw new PasskeyError('unsupported', err.message)
    throw err
  }
  if (!credential) throw new PasskeyError('cancelled', 'No passkey was given.')
  return credential as PublicKeyCredential
}

/** What to tell the person when this place has no passkeys to use: the Mac app's system, or the browser. */
export function noPasskeysText(): string {
  return inMacShell() ? 'This Mac cannot use passkeys.' : 'This browser cannot use passkeys.'
}
